<template>
  <ModalTabs :tabs="tabs" @onSetTab="setActiveTab" />
  <div :class="`lf-modal__content-tab lf-modal__content-tab--${stringToSlug(selectedTab)}`">
    <div
      class="lf-modal-wrapper lf-modal-wrapper--with-sidebar"
      :class="{
        'lf-modal-wrapper--loading': isLoadingCategories ?? isLoadingAddons,
        'lf-modal-wrapper--saving': isSaving
      }"
    >
      <div class="lf-modal-wrapper__sidebar">
        <div class="lf-modal-wrapper__sidebar-inner">
          <transition name="fade">
            <ModalSidebarCategories
              v-if="categories && categories.length > 0"
              :active-category="selectedCategory"
              :categories="categories"
              @setActiveCategory="onUpdateCategory"
            />
          </transition>
        </div>
      </div>

      <div class="lf-modal-wrapper__content">
        <div class="lf-modal-wrapper__content-inner">
          <transition name="fade">
            <ModalAddons
              v-if="addons && addons?.length > 0"
              :categories="categories"
              :addons="addons"
              @addItem="onAddItem"
            />
          </transition>

          <div
            v-if="addons?.length === 0 && !isLoadingAddons && !isLoadingCategories"
            class="lf-modal-wrapper__content-empty"
          >
            <i class="lf-modal-wrapper__content-empty-icon icon-empty-circle"></i>
            No addons
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, PropType } from 'vue';
import ModalTabs from '@/components/components/modals/ModalTabs.vue';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import ModalSidebarCategories from '@/components/components/modals/ModalSidebarCategories.vue';
import { stringToSlug } from '@/utilities/Utilities';
import ColumnModel from '@/components/layout/column/ColumnModel';
import ModalAddons from '@/components/components/editing/Modals/Content/ModalAddons.vue';
import { useStore } from '@/store';
import { CampaignState } from '@/models/CampaignModel';
import { APICategory, Addon, TabItem } from '@/components/components/editing/Modals/interfaces';

const tabItems: TabItem[] = [
  {
    id: 1,
    label: 'Element',
    category: 'element'
  },
  {
    id: 2,
    label: 'Addons',
    category: 'addon'
  }
];

export default defineComponent({
  name: 'ModalContentAddAddon',
  components: {
    ModalAddons,
    ModalSidebarCategories,
    ModalTabs
  },
  inheritAttrs: false,
  props: {
    columnModel: {
      type: Object as PropType<ColumnModel>,
      required: true
    }
  },
  emits: ['close-modal'],
  setup(props, context) {
    let selectedTab = ref('');
    if (tabItems && tabItems[0].label) {
      selectedTab.value = tabItems[0].label.toLocaleLowerCase();
    }
    const selectedCategory = ref<string | number>('all');
    const store = useStore();
    const campaignState = store.getters['campaign/state'] as CampaignState;
    const tabs = ref<TabItem[]>(tabItems);
    const isLoadingCategories = ref(true);
    const isLoadingAddons = ref(true);
    const isSaving = ref(false);
    const categories = ref<APICategory[]>([]);
    const addons = ref<Addon[]>([]);
    const defaultParameters = reactive({
      category: 'element',
      pageId: props.columnModel.row.section.state.id
    });
    const subCategory = ref<string | number>('all');

    let isAdding = false;

    const onFetchCategories = async () => {
      const categoriesUrl = `${ConfigData.adminUiEndpoint}/api/v1/campaign/edit/addonSubcategories?campaign_id=${
        campaignState.id
      }&page_id=${defaultParameters.pageId}&category=${encodeURIComponent(defaultParameters.category)}&vue=1`;
      const { fetchData } = useAxios<APICategory[]>(categoriesUrl);
      const response = await fetchData();

      isLoadingCategories.value = false;
      categories.value = response;
    };

    const onFetchAddons = async () => {
      const addonsUrl = `${ConfigData.adminUiEndpoint}/api/v1/campaign/edit/addonsByCategory?campaign_id=${
        campaignState.id
      }&page_id=${defaultParameters.pageId}&category=${encodeURIComponent(
        defaultParameters.category
      )}&subcategory=${encodeURIComponent(subCategory.value)}&vue=1`;
      const { fetchData } = useAxios<Addon[]>(addonsUrl);
      const response = await fetchData();

      isLoadingAddons.value = false;

      if (response) {
        addons.value = Object.values(response).map((addon) => {
          return {
            faq: addon.faq,
            icon: addon.icon,
            id: addon.id,
            label: addon.label
          };
        });
      }
    };

    const setActiveTab = async (value: TabItem) => {
      categories.value = [];
      addons.value = [];
      isLoadingCategories.value = true;
      isLoadingAddons.value = true;
      selectedCategory.value = 'all';
      subCategory.value = 'all';
      if (value && value.category) {
        defaultParameters.category = value.category;
      }
      await onFetchCategories();
      await onFetchAddons();
      if (value && value.label) {
        selectedTab.value = value.label.toLocaleLowerCase();
      }
    };

    const onUpdateCategory = async (categoryId: string) => {
      subCategory.value = categoryId;
      await onFetchAddons();
      selectedCategory.value = categoryId;
    };

    const onAddItem = async (addonId: string) => {
      if (isAdding) {
        return;
      }

      isAdding = true;
      await props.columnModel.addAddon(addonId, null);
      isAdding = false;
      context.emit('close-modal');
    };

    onMounted(() => {
      onFetchCategories();
      onFetchAddons();
    });

    return {
      onUpdateCategory,
      selectedCategory,
      selectedTab,
      addons,
      categories,
      tabs,
      isLoadingCategories,
      isLoadingAddons,
      isSaving,
      setActiveTab,
      stringToSlug,
      onAddItem
    };
  }
});
</script>
